import { getDisplayRows } from './heatmapViewPreset';
import { getPercentileBounds } from './viridis';
import type { ExposureRow, ExposureType, Ticker } from '@/store/heatmapStore';

const PERCENTILE_TICKERS: Ticker[] = ['SPY', 'QQQ', 'IWM'];
const ZERO_ONLY_STRIKE_FILTER_TICKERS = new Set<Ticker>(['SPY', 'SPX', 'QQQ', 'IWM']);

export interface HeatmapKingCell {
  rowIndex: number;
  expiration: string;
}

export interface HeatmapColorScale {
  colorMin: number;
  colorMax: number;
  kingCell: HeatmapKingCell | null;
  spotRowIndex: number;
}

export function formatExpirationLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function findSpotRowIndex(rows: readonly ExposureRow[], spotPrice: number): number {
  let closest = 0;
  let minDist = Infinity;
  for (let i = 0; i < rows.length; i++) {
    const dist = Math.abs(rows[i].strike - spotPrice);
    if (dist < minDist) {
      minDist = dist;
      closest = i;
    }
  }
  return closest;
}

function hasOnlyExplicitZeros(row: ExposureRow, expirations: readonly string[]): boolean {
  return (
    expirations.length > 0 && expirations.every((expiration) => row.cells[expiration]?.raw === 0)
  );
}

export function selectHeatmapDisplayRows(
  rows: ExposureRow[],
  expirations: readonly string[],
  spotPrice: number,
  ticker: Ticker | undefined,
  visibleStrikeCount: number | null | undefined,
): ExposureRow[] {
  const filteredRows =
    ticker != null && ZERO_ONLY_STRIKE_FILTER_TICKERS.has(ticker)
      ? rows.filter((row) => !hasOnlyExplicitZeros(row, expirations))
      : rows;

  return getDisplayRows(filteredRows, spotPrice, visibleStrikeCount);
}

export function computeHeatmapColorScale(
  displayRows: readonly ExposureRow[],
  expirations: readonly string[],
  spotPrice: number,
  type: ExposureType,
  ticker: Ticker | undefined,
): HeatmapColorScale {
  let min = Infinity;
  let max = -Infinity;
  let kingMagnitude = Number.NEGATIVE_INFINITY;
  let kingCell: HeatmapKingCell | null = null;
  const allValues: number[] = [];

  for (let rowIndex = 0; rowIndex < displayRows.length; rowIndex++) {
    const row = displayRows[rowIndex];
    for (const exp of expirations) {
      const cell = row.cells[exp];
      if (cell != null) {
        if (cell.raw < min) min = cell.raw;
        if (cell.raw > max) max = cell.raw;
        allValues.push(cell.raw);
        const magnitude = Math.abs(cell.raw);
        if (magnitude > kingMagnitude) {
          kingMagnitude = magnitude;
          kingCell = { rowIndex, expiration: exp };
        }
      }
    }
  }

  const globalMin = Number.isFinite(min) ? min : 0;
  const globalMax = Number.isFinite(max) ? max : 0;

  const usePercentile = type === 'vex' && ticker != null && PERCENTILE_TICKERS.includes(ticker);
  let [colorMin, colorMax] = usePercentile
    ? getPercentileBounds(allValues, 0.01, 0.99)
    : [globalMin, globalMax];

  // Safety: if the percentile clip collapses (e.g., sparse data where the central
  // 96% of cells share the same value), fall back to absolute bounds to avoid the
  // very monochrome failure mode we are trying to fix.
  if (usePercentile && colorMin === colorMax) {
    colorMin = globalMin;
    colorMax = globalMax;
  }

  return {
    colorMin,
    colorMax,
    kingCell,
    spotRowIndex: findSpotRowIndex(displayRows, spotPrice),
  };
}
